<template>
    <div class="department-bonus-conditions">
        <div class="mb-4">
            <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-3">Бонусные условия департамента</h6>
            
                <button 
                    v-if="!readOnly"
                    type="button" 
                    class="btn btn-primary btn-sm mb-3"
                    @click="toggleEditMode"
                >
                    {{ isEditMode ? 'Отменить' : 'Редактировать условия' }}
                </button>
            </div>

            <!-- Режим просмотра -->
            <div v-if="!isEditMode && (bonusConditions.length > 0 || (isTODepartment && toConditions))" class="bonus-conditions-display">
                <!-- Детальная информация о системе мотивации для ТО/ВЭД -->
                <div v-if="isTODepartment" class="alert alert-info mb-3">
                    <h6>Система мотивации отдела ВЭД</h6>
                    
                    <!-- Квартальные планы -->
                    <div class="mb-3" v-if="toConditions && toConditions.quarterly_plans">
                        <h6 class="text-primary">Квартальные планы:</h6>
                        <div class="row">
                            <div class="col-md-6" v-for="(quarter, quarterKey) in toConditions.quarterly_plans" :key="quarterKey">
                                <div class="card mb-2">
                                    <div class="card-header py-2">
                                        <h6 class="mb-0">{{ getQuarterName(quarterKey) }}</h6>
                                    </div>
                                    <div class="card-body py-2">
                                        <ul class="mb-0 small">
                                            <li><strong>{{ quarter.min_declarations || 90 }} деклараций</strong> за квартал</li>
                                            <li><strong>{{ formatNumber(quarter.min_margin_per_employee || 3000000) }} руб.</strong> маржинальности на сотрудника</li>
                                            <li><strong>{{ formatNumber(quarter.min_department_margin || 15500000) }} руб.</strong> маржинальности отдела</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Базовые условия (fallback) -->
                    <div class="mb-3" v-if="toConditions && toConditions.base_conditions && !toConditions.quarterly_plans">
                        <h6 class="text-primary">Базовые условия для получения бонуса:</h6>
                        <ul class="mb-0">
                            <li><strong>{{ toConditions.base_conditions.min_declarations || 90 }} деклараций</strong> за квартал ИЛИ</li>
                            <li><strong>{{ formatNumber(toConditions.base_conditions.min_margin_per_employee || 3000000) }} руб.</strong> маржинальности на сотрудника за квартал</li>
                            <li><strong>{{ formatNumber(toConditions.base_conditions.min_department_margin || 15500000) }} руб.</strong> маржинальности отдела за квартал</li>
                        </ul>
                    </div>
                    
                    <!-- Проценты бонусов -->
                    <div class="mb-3" v-if="toConditions && toConditions.bonus_percentages">
                        <h6 class="text-primary">Проценты бонусов:</h6>
                        <div class="row">
                            <div class="col-md-6">
                                <ul class="mb-0">
                                    <li><strong>{{ toConditions.bonus_percentages.new_client_bonus || 20 }}%</strong> от маржинальность по новым клиентам</li>
                                    <li><strong>{{ toConditions.bonus_percentages.employee_super_result || 5 }}%</strong> от сверхрезультата сотрудника</li>
                                </ul>
                            </div>
                            <div class="col-md-6">
                                <ul class="mb-0">
                                    <li><strong>{{ toConditions.bonus_percentages.employee_super_result || 5 }}%</strong> от сверхрезультата руководителя (как у сотрудника)</li>
                                    <li><strong>{{ toConditions.bonus_percentages.head_department_super || 10 }}%</strong> от сверхрезультата отдела для руководителя</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Формулы расчета -->
                    <div v-if="toConditions && toConditions.bonus_percentages">
                        <h6 class="text-primary">Формулы расчета:</h6>
                        <ul class="mb-0">
                            <li><strong>Бонус сотрудника:</strong> (Сверхрезультат × {{ toConditions.bonus_percentages.employee_super_result || 5 }}%) + (Маржинальность по новым клиентам × {{ toConditions.bonus_percentages.new_client_bonus || 20 }}%)</li>
                            <li><strong>Бонус руководителя:</strong> (Личный сверхрезультат × {{ toConditions.bonus_percentages.employee_super_result || 5 }}%) + (Сверхрезультат отдела × {{ toConditions.bonus_percentages.head_department_super || 10 }}%)</li>
                            <li><strong>Сверхрезультат:</strong> Фактическая маржинальность - Окупаемость</li>
                        </ul>
                    </div>
                </div>
                
                <!-- Таблица условий только для логистики -->
                <div v-if="!isTODepartment" class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="thead-light">
                            <tr>
                                <th style="height: 40px;">Эффективность (от %)</th>
                                <th style="height: 40px;">Эффективность (до %)</th>
                                <th style="height: 40px;">Бонусный процент (%)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(condition, index) in bonusConditions" :key="index">
                                <td>{{ condition.efficiency_min }}</td>
                                <td>{{ condition.efficiency_max }}</td>
                                <td>{{ condition.percentage }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Режим редактирования для логистики -->
            <div v-if="isEditMode && !isTODepartment && !readOnly" class="bonus-conditions-edit">
                <div class="form-group">
                    <!-- Заголовки колонок -->
                    <div class="row mb-2">
                        <div class="col-md-3">
                            <label class="form-label text-muted small">Эффективность (от %)</label>
                        </div>
                        <div class="col-md-3">
                            <label class="form-label text-muted small">Эффективность (до %)</label>
                        </div>
                        <div class="col-md-3">
                            <label class="form-label text-muted small">Бонусный процент (%)</label>
                        </div>
                        <div class="col-md-3">
                            <label class="form-label text-muted small">Действие</label>
                        </div>
                    </div>
                    
                    <div v-for="(condition, index) in editedBonusConditions" :key="index" class="row mb-2">
                        <div class="col-md-3">
                            <b-form-input 
                                v-model="condition.efficiency_min" 
                                type="number" 
                                step="0.01"
                                class="form-control" 
                                placeholder="От %"
                                size="sm"
                            />
                        </div>
                        <div class="col-md-3">
                            <b-form-input 
                                v-model="condition.efficiency_max" 
                                type="number" 
                                step="0.01"
                                class="form-control" 
                                placeholder="До %"
                                size="sm"
                            />
                        </div>
                        <div class="col-md-3">
                            <b-form-input 
                                v-model="condition.percentage" 
                                type="number" 
                                step="0.01"
                                class="form-control" 
                                placeholder="Процент %"
                                size="sm"
                            />
                        </div>
                        <div class="col-md-3">
                            <b-button 
                                @click="removeBonusCondition(index)"
                                v-if="editedBonusConditions.length > 1"
                                variant="danger"
                                size="sm"
                                style="height: 33px;"
                            >
                                <b-icon icon="trash"></b-icon>
                            </b-button>
                        </div>
                    </div>
                </div>

                <!-- Кнопки сохранения/отмены -->
                <div class="mt-3 text-right">
                    <b-button 
                        variant="secondary"
                        class="mr-2" 
                        @click="addBonusCondition"
                    >
                        Добавить условие
                    </b-button>

                    <b-button 
                        variant="success"
                        class="mr-2"
                        @click="saveBonusConditions"
                        :disabled="isLoading"
                    >
                        {{ isLoading ? 'Сохранение...' : 'Сохранить' }}
                    </b-button>
                    <b-button 
                        variant="secondary"
                        @click="cancelEdit"
                    >
                        Отменить
                    </b-button>
                </div>
            </div>

            <!-- Режим редактирования для ТО/ВЭД -->
            <div v-if="isEditMode && isTODepartment && !readOnly" class="to-conditions-edit">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Настройка условий мотивации для отдела ТО/ВЭД</h6>
                    </div>
                    <div class="card-body">
                        <!-- Выбор квартала -->
                        <div class="mb-4">
                            <h6 class="text-primary">Выберите квартал для настройки</h6>
                            <div class="row">
                                <div class="col-md-6">
                                    <label class="form-label">Квартал</label>
                                    <b-form-select 
                                        v-model="selectedQuarter" 
                                        :options="quarterOptions"
                                        size="sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Квартальные условия -->
                        <div class="mb-4">
                            <h6 class="text-primary">Условия для {{ getQuarterName(selectedQuarter) }}</h6>
                            <div class="row">
                                <div class="col-md-4">
                                    <label class="form-label">Минимум деклараций за квартал</label>
                                    <b-form-input 
                                        v-model="editedTOConditions.quarterly_plans[selectedQuarter].min_declarations" 
                                        type="number" 
                                        size="sm"
                                        v-if="editedTOConditions.quarterly_plans && editedTOConditions.quarterly_plans[selectedQuarter]"
                                    />
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label">Минимум маржинальности на сотрудника (руб.)</label>
                                    <b-form-input 
                                        v-model="editedTOConditions.quarterly_plans[selectedQuarter].min_margin_per_employee" 
                                        type="number" 
                                        size="sm"
                                        v-if="editedTOConditions.quarterly_plans && editedTOConditions.quarterly_plans[selectedQuarter]"
                                    />
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label">Минимум маржинальности отдела (руб.)</label>
                                    <b-form-input 
                                        v-model="editedTOConditions.quarterly_plans[selectedQuarter].min_department_margin" 
                                        type="number" 
                                        size="sm"
                                        v-if="editedTOConditions.quarterly_plans && editedTOConditions.quarterly_plans[selectedQuarter]"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Проценты бонусов -->
                        <div class="mb-4">
                            <h6 class="text-primary">Проценты бонусов</h6>
                            <div class="row">
                                <div class="col-md-4">
                                    <label class="form-label">Бонус за нового клиента (%)</label>
                                    <b-form-input 
                                        v-model="editedTOConditions.bonus_percentages.new_client_bonus" 
                                        type="number" 
                                        step="0.01"
                                        size="sm"
                                    />
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label">Бонус от сверхрезультата сотрудника (%)</label>
                                    <b-form-input 
                                        v-model="editedTOConditions.bonus_percentages.employee_super_result" 
                                        type="number" 
                                        step="0.01"
                                        size="sm"
                                    />
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label">Бонус руководителя от отдела (%)</label>
                                    <b-form-input 
                                        v-model="editedTOConditions.bonus_percentages.head_department_super" 
                                        type="number" 
                                        step="0.01"
                                        size="sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Кнопки сохранения/отмены -->
                        <div class="text-right">
                            <b-button 
                                variant="success"
                                class="mr-2"
                                @click="saveTOConditions"
                                :disabled="isLoading"
                            >
                                {{ isLoading ? 'Сохранение...' : 'Сохранить условия' }}
                            </b-button>
                            <b-button 
                                variant="secondary"
                                @click="cancelEdit"
                            >
                                Отменить
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Сообщение об отсутствии условий -->
            <div v-if="!isEditMode && bonusConditions.length === 0 && !isTODepartment" class="alert alert-info">
                Бонусные условия не настроены. Нажмите "Редактировать условия" для настройки.
            </div>
            
            <!-- Сообщение об отсутствии условий для ТО -->
            <div v-if="!isEditMode && isTODepartment && !toConditions" class="alert alert-info">
                Условия мотивации для отдела ТО/ВЭД не настроены.
            </div>
        </div>
    </div>
</template>

<script>
import { API_DEPARTMENTS } from '@/constants';

export default {
    name: 'DepartmentBonusConditions',
    props: {
        departmentId: {
            type: Number,
            required: true
        },
        readOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            bonusConditions: [],
            editedBonusConditions: [],
            toConditions: null,
            editedTOConditions: {
                quarterly_plans: {
                    'Q1': {
                        min_declarations: 90,
                        min_margin_per_employee: 3000000,
                        min_department_margin: 15500000
                    },
                    'Q2': {
                        min_declarations: 90,
                        min_margin_per_employee: 3000000,
                        min_department_margin: 15500000
                    },
                    'Q3': {
                        min_declarations: 90,
                        min_margin_per_employee: 3000000,
                        min_department_margin: 15500000
                    },
                    'Q4': {
                        min_declarations: 90,
                        min_margin_per_employee: 3000000,
                        min_department_margin: 15500000
                    }
                },
                base_conditions: {
                    min_declarations: 90,
                    min_margin_per_employee: 3000000,
                    min_department_margin: 15500000
                },
                bonus_percentages: {
                    new_client_bonus: 20,
                    employee_super_result: 5,
                    head_department_super: 10
                }
            },
            selectedQuarter: 'Q1',
            isEditMode: false,
            isLoading: false
        }
    },
    computed: {
        isTODepartment() {
            // Проверяем, является ли департамент ТО/ВЭД
            return this.departmentId === 2;
        },
        quarterOptions() {
            return [
                { value: 'Q1', text: 'Q1 (Февраль-Апрель)' },
                { value: 'Q2', text: 'Q2 (Май-Июль)' },
                { value: 'Q3', text: 'Q3 (Август-Октябрь)' },
                { value: 'Q4', text: 'Q4 (Ноябрь-Январь)' }
            ];
        }
    },
    mounted() {
        this.loadBonusConditions();
    },
    methods: {
        async loadBonusConditions() {
            try {
                if (this.isTODepartment) {
                    // Загружаем условия для ТО/ВЭД
                    const response = await axios.get(`/api/motivation2025/to/conditions/${this.departmentId}`);
                    this.toConditions = response.data;
                } else {
                    // Загружаем условия для логистики
                    const response = await axios.get(`${API_DEPARTMENTS}/${this.departmentId}/bonus-conditions`);
                    this.bonusConditions = response.data.bonus_conditions || [];
                }
            } catch (error) {
                console.error('Ошибка загрузки бонусных условий:', error);
                this.bonusConditions = [];
                this.toConditions = null;
            }
        },

        toggleEditMode() {
            if (this.readOnly) {
                return;
            }
            this.isEditMode = !this.isEditMode;
            if (this.isEditMode) {
                if (this.isTODepartment) {
                    // Для ТО копируем текущие условия или создаем базовые
                    if (this.toConditions && this.toConditions.quarterly_plans) {
                        this.editedTOConditions = JSON.parse(JSON.stringify(this.toConditions));
                    } else {
                        // Создаем базовую структуру с квартальными планами
                        this.editedTOConditions = {
                            quarterly_plans: {
                                'Q1': {
                                    min_declarations: 90,
                                    min_margin_per_employee: 3000000,
                                    min_department_margin: 15500000
                                },
                                'Q2': {
                                    min_declarations: 90,
                                    min_margin_per_employee: 3000000,
                                    min_department_margin: 15500000
                                },
                                'Q3': {
                                    min_declarations: 90,
                                    min_margin_per_employee: 3000000,
                                    min_department_margin: 15500000
                                },
                                'Q4': {
                                    min_declarations: 90,
                                    min_margin_per_employee: 3000000,
                                    min_department_margin: 15500000
                                }
                            },
                            base_conditions: {
                                min_declarations: 90,
                                min_margin_per_employee: 3000000,
                                min_department_margin: 15500000
                            },
                            bonus_percentages: {
                                new_client_bonus: 20,
                                employee_super_result: 5,
                                head_department_super: 10
                            }
                        };
                    }
                } else {
                    // Для логистики используем старую логику
                    this.editedBonusConditions = JSON.parse(JSON.stringify(this.bonusConditions));
                    if (this.editedBonusConditions.length === 0) {
                        this.addBonusCondition();
                    }
                }
            }
        },

        addBonusCondition() {
            this.editedBonusConditions.push({
                efficiency_min: 0,
                efficiency_max: 0,
                percentage: 0
            });
        },

        removeBonusCondition(index) {
            if (this.editedBonusConditions.length > 1) {
                this.editedBonusConditions.splice(index, 1);
            }
        },

        async saveBonusConditions() {
            this.isLoading = true;
            try {
                const response = await axios.post(`${API_DEPARTMENTS}/${this.departmentId}/bonus-conditions`, {
                    bonus_conditions: this.editedBonusConditions
                });
                
                this.bonusConditions = response.data.bonus_conditions;
                this.isEditMode = false;
                this.$toast.success('Бонусные условия сохранены');
            } catch (error) {
                console.error('Ошибка сохранения бонусных условий:', error);
                this.$toast.error('Ошибка сохранения бонусных условий');
            } finally {
                this.isLoading = false;
            }
        },

        cancelEdit() {
            this.isEditMode = false;
            this.editedBonusConditions = [];
            this.editedTOConditions = {
                quarterly_plans: {
                    'Q1': {
                        min_declarations: 90,
                        min_margin_per_employee: 3000000,
                        min_department_margin: 15500000
                    },
                    'Q2': {
                        min_declarations: 90,
                        min_margin_per_employee: 3000000,
                        min_department_margin: 15500000
                    },
                    'Q3': {
                        min_declarations: 90,
                        min_margin_per_employee: 3000000,
                        min_department_margin: 15500000
                    },
                    'Q4': {
                        min_declarations: 90,
                        min_margin_per_employee: 3000000,
                        min_department_margin: 15500000
                    }
                },
                base_conditions: {
                    min_declarations: 90,
                    min_margin_per_employee: 3000000,
                    min_department_margin: 15500000
                },
                bonus_percentages: {
                    new_client_bonus: 20,
                    employee_super_result: 5,
                    head_department_super: 10
                }
            };
        },

        async saveTOConditions() {
            this.isLoading = true;
            try {
                const response = await axios.post(`${API_DEPARTMENTS}/${this.departmentId}/bonus-conditions`, {
                    bonus_conditions: this.editedTOConditions
                });
                
                // Перезагружаем данные для обновления отображения
                this.loadBonusConditions();
                this.isEditMode = false;
                this.$toast.success('Условия мотивации для ТО/ВЭД сохранены');
            } catch (error) {
                console.error('Ошибка сохранения условий ТО:', error);
                this.$toast.error('Ошибка сохранения условий ТО/ВЭД');
            } finally {
                this.isLoading = false;
            }
        },

        formatNumber(number) {
            return new Intl.NumberFormat('ru-RU').format(number);
        },

        getQuarterName(quarter) {
            const names = {
                'Q1': 'Q1 (Февраль-Апрель)',
                'Q2': 'Q2 (Май-Июль)',
                'Q3': 'Q3 (Август-Октябрь)',
                'Q4': 'Q4 (Ноябрь-Январь)'
            };
            return names[quarter] || quarter;
        }
    }
}
</script>

<style scoped>
.department-bonus-conditions {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}

.bonus-conditions-display table {
    margin-bottom: 0;
}

.bonus-conditions-edit .form-group {
    background: white;
    padding: 15px;
    border-radius: 5px;
    border: 1px solid #dee2e6;
}

.to-conditions-edit .card {
    border: 1px solid #dee2e6;
    border-radius: 8px;
}

.to-conditions-edit .card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.to-conditions-edit .form-label {
    font-weight: 500;
    color: #495057;
    margin-bottom: 5px;
}
</style>
